import { Inject, Injectable, Optional, Provider, inject } from '@angular/core';
import { IQueryConfig, IQueryResponse, IQueryResponseSentrynel, IQuerySentrynelConfig, QUERY__SENTRYNEL_CONFIG, QUERY_CONFIG, QueryState, QueryStateSentrynel } from '../models/query.model';
import { SignalStore, SignalStoreConfig } from 'src/app/shared/functions/store.functions';
import { queryStateConfig, queryStateConfigSentrynel } from 'src/app/config/query.config';
import { deepMerge } from 'src/app/shared/functions/object.function';
import { toObservable } from '@angular/core/rxjs-interop';
import { IPageEvent } from '../models/paginator.model';
import { ISort, ISortEvent } from '../models/sort.model';
import { ISearchEvent } from '../models/table.model';
import { IFilterEvent, IFilters } from '../models/filters.model';
import { ApiSentrynelService } from './api-sentrynel.service';

@Injectable()
export class QuerySentrynelService<T> {
  store: SignalStore<QueryState<T>>;

  constructor(
    @Inject('endpoint') private endpoint: string,
    @Optional() @Inject(QUERY__SENTRYNEL_CONFIG) private readonly userConfig: SignalStoreConfig<QueryStateSentrynel<T>>,
    @Inject(ApiSentrynelService) private readonly apiSentrynelService: ApiSentrynelService,
  ) {
    this.defaultConfig = deepMerge(this.defaultConfig, this.userConfig);
    this.store = new SignalStore<QueryStateSentrynel<T>>(this.endpoint + '-store', this.defaultConfig);
    toObservable(this.store.selectKey('sort')).subscribe(() => {
      this.setPageIndex(0);
    });
    toObservable(this.store.selectKey('search')).subscribe(() => {
      this.setPageIndex(0);
    });
    toObservable(this.store.selectKey('filters')).subscribe(() => {
      this.setPageIndex(0);
    });
  }

  private defaultConfig = queryStateConfigSentrynel;

  private setPageIndex(index: number) {
    this.store.setKey('pageable', {
      ...this.store.selectKey('pageable')(),
      ...{
        page: index
      }
    });
  }

  private cleanUpFilterField(field: string) {
    let result: string = field;
    result = result.replaceAll('_', '.');
    return result;
  }

  private cleanUpFilterValue(value: string) {
    let result: string = `${value}`;
    result = result.replaceAll('T00:00:00.000+01:00', '');
    return result;
  }

  private getUrl(endpoint: string, config?: IQuerySentrynelConfig) {
    if(config?.meta?.page){
       let datoPage = config?.meta?.page + 1
       config.meta.page=datoPage
    }
   
    let url = `${endpoint}?page=${config?.meta?.page ?? 0}&limit=${config?.meta?.itemsPerPage ?? this.defaultConfig.initState?.meta?.itemsPerPage}`;

    //sort
    let sort = config?.sort ?? this.defaultConfig.initState?.sort;
    if (sort) {
      url += `&sortBy=${sort.field}:${sort.direction.toUpperCase()}`;
    }
    //search
    if (config?.search) {
      url += `&generic.contains=${config.search}`;
    }
    //filters
    for (const key in config?.filters) {
      if (config?.filters.hasOwnProperty(key) && config?.filters[key] !== null) {
        url += `&${this.cleanUpFilterField(key)}=${this.cleanUpFilterValue(config?.filters[key])}`;
      }
    }
    return url;
  }

  get list() {
    return this.store.selectKey('list');
  }

  get search() {
    return this.store.selectKey('search');
  }

  get filters() {
    return this.store.selectKey('filters');
  }

  get sort() {
    return this.store.selectKey('sort');
  }

  get pageable() {
    return this.store.selectKey('pageable');
  }

  onSort(sortEvent: ISortEvent) {
    let sort: ISort | undefined = sortEvent.direction !== '' ? { field: sortEvent.active, direction: sortEvent.direction } : undefined;
    this.store.setKey('sort', sort);
  }

  onSearch(searchEvent: ISearchEvent) {
    let search = searchEvent.value && searchEvent.value !== '' ? searchEvent.value : undefined;
    this.store.setKey('search', search);
  }

  onFilter(filterEvent: IFilterEvent) {
    this.store.setKey('filters', filterEvent);
  }

  onPage(pageEvent: IPageEvent) {
    this.store.setKey('pageable', {
      ...this.store.selectKey('pageable')(),
      ...{
        page: pageEvent.pageIndex,
        size: pageEvent.pageSize,
      }
    });
  }

  get(params?: IQuerySentrynelConfig, callback?: () => void) {
    if (this.store.selectKey('isLoading')() === false) {
      this.store.setKey('isLoading', true);
      let url = this.getUrl(this.endpoint, params)
      this.apiSentrynelService.get<IQueryResponseSentrynel>(url).subscribe((response) => {
        this.store.setKey('list', response.data);
        this.store.setKey('pageable', {
          currentPage: response.meta.currentPage-1,
          itemsPerPage: response.meta.itemsPerPage,
          totalItems: response.meta.totalItems,
          totalPages: response.meta.totalPages,
        });
        if (callback) {
          callback();
        }
      }).add(() => {
        this.store.setKey('isLoading', false);
      })
    }
  }

  getConcat(params?: IQuerySentrynelConfig, callback?: () => void) {
    if (this.store.selectKey('isLoading')() === false) {
      this.store.setKey('isLoading', true);
      let url = this.getUrl(this.endpoint, params)
      this.apiSentrynelService.get<IQueryResponseSentrynel>(url).subscribe((response) => {
        this.store.setKey('list',   this.store.selectKey('list')().concat(response.data));
        this.store.setKey('pageable', {
          currentPage: response.meta.currentPage,
          itemsPerPage: response.meta.itemsPerPage,
          totalItems: response.meta.totalItems,
          totalPages: response.meta.totalPages,
        });
        if (callback) {
          callback();
        }
      }).add(() => {
        this.store.setKey('isLoading', false);
      })
    }
  } 


  getAll(filters?: IFilters) {
    this.get({ meta: { totalItems: 99999 }, filters: filters });
  }
}

export function provideQuerySentrynelService<T>(
  endpoint: string,
  userConfig?: SignalStoreConfig<QueryStateSentrynel<T>>,
): Provider[] {
  return [
    {
      provide: createTokenQueryServiceInstance(endpoint),
      useFactory: () => new QuerySentrynelService<T>(endpoint, userConfig ?? queryStateConfigSentrynel, inject(ApiSentrynelService))
    }
  ]
}

export function createTokenQueryServiceInstance(endpoint: string) {
  return `${endpoint}-query-service`;
}
